import { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToastReturn } from '../../contexts/hooks/use-toast';
import classNames from '../../utils/classnames';
import FontAwesomeIcon from '../../ui/font-awesome-icon/font-awesome-icon';
import './toast-notification.scss';

export type ToastNotificationProps = useToastReturn & { className?: string };

const ToastNotification: FC<ToastNotificationProps> = (props) => {
    const { isOpen, message, closeAfter = 5000, type = 'success', close, variant } = props;

    const [iconProps, setIconProps] = useState<{ name: string; theme?: string } | null>(null);

    const isInteractiveStyle = variant === 'interactive';
    const shouldShowStatusIcon = isInteractiveStyle && Boolean(iconProps);

    const statusIcon = {
        success: { name: 'checkmark-circle', theme: 'green' },
        error: { name: 'warning-triangle', theme: 'red' },
        info: { name: 'info-circle', theme: 'teal' }
    };

    useEffect(() => {
        if (!isInteractiveStyle) {
            return;
        }

        if (!isOpen) {
            // 1s to match the opacity transition effect
            setTimeout(() => setIconProps(null), 1000);

            return;
        }

        setIconProps(statusIcon[props.type as string] ?? null);
    }, [props.type, isOpen, isInteractiveStyle]);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        if (isOpen) {
            timeoutId = setTimeout(close, closeAfter);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isOpen, closeAfter, message]);

    return createPortal(
        <div className={classNames(`toast-container -${props.type}`, props.className, variant)}>
            <div onClick={close} className={`toast ${type} ${isOpen ? 'displayed' : 'hidden'}`}>
                {shouldShowStatusIcon && <FontAwesomeIcon name={iconProps!.name} theme={iconProps!.theme} />}
                {message}
            </div>
        </div>,
        document.getElementById('modal-root')!
    );
};

export default ToastNotification;
